import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useActorEmails, fmtAuthor } from "@/hooks/use-actor-emails";
import { optimizeImage } from "@/lib/image-optim";

export const Route = createFileRoute("/_authenticated/admin/hero")({
  
  head: () => ({ meta: [{ title: "Admin — Hero | Mine Class" }, { name: "description", content: "Panel de administración de Hero — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: HeroAdmin,
});

type Slide = {
  id: string;
  tag: string | null;
  title: string;
  subtitle: string | null;
  image_url: string;
  cta_label: string | null;
  cta_href: string | null;
  sort_order: number;
  is_active: boolean;
  title_size: string | null;
  duration_ms: number | null;
  created_by: string | null;
  updated_by: string | null;
};

const INTERNAL_LINK_GROUPS: { label: string; items: { label: string; href: string }[] }[] = [
  {
    label: "General",
    items: [
      { label: "Home", href: "/" },
      { label: "Contacto", href: "/contacto" },
      { label: "Preguntas Frecuentes", href: "/preguntas-frecuentes" },
      { label: "Carrito", href: "/carrito" },
    ],
  },
  {
    label: "Quiénes Somos",
    items: [
      { label: "Qué es Mine Class", href: "/quienes-somos/que-es" },
      { label: "Equipo", href: "/quienes-somos/equipo" },
      { label: "Alianzas", href: "/quienes-somos/alianzas" },
    ],
  },
  {
    label: "Soluciones — Acreditación",
    items: [
      { label: "Acreditación (índice)", href: "/soluciones/acreditacion-contratistas" },
      { label: "Mine Pass", href: "/soluciones/acreditacion-contratistas/mine-pass" },
      { label: "Examine", href: "/soluciones/acreditacion-contratistas/examine" },
    ],
  },
  {
    label: "Soluciones — Tecnología de Aprendizaje",
    items: [
      { label: "Tecnología (índice)", href: "/soluciones/tecnologia-aprendizaje" },
      { label: "Athena LMS", href: "/soluciones/tecnologia-aprendizaje/athena-lms" },
      { label: "FaceID", href: "/soluciones/tecnologia-aprendizaje/face-id" },
      { label: "VigIA", href: "/soluciones/tecnologia-aprendizaje/vigia" },
      { label: "SonIA", href: "/soluciones/tecnologia-aprendizaje/sonia" },
    ],
  },
  {
    label: "Cursos",
    items: [
      { label: "Cursos (índice)", href: "/cursos" },
      { label: "Cursos para Empresas", href: "/cursos/empresas" },
      { label: "Programas a medida", href: "/cursos/empresas/programas-a-medida" },
      { label: "Cursos para Personas", href: "/cursos/personas" },
      { label: "Convenio U. Mayor", href: "/cursos/personas/convenio-umayor" },
    ],
  },
  {
    label: "Clientes",
    items: [
      { label: "Clientes", href: "/clientes" },
      { label: "Casos de éxito", href: "/clientes/casos-exito" },
    ],
  },
  {
    label: "Comunicaciones",
    items: [
      { label: "Noticias", href: "/comunicaciones/noticias" },
      { label: "Newsletter", href: "/comunicaciones/newsletter" },
      { label: "Podcast", href: "/comunicaciones/podcast" },
      { label: "Media", href: "/comunicaciones/media" },
      { label: "Recursos", href: "/comunicaciones/recursos" },
    ],
  },
];
const INTERNAL_LINKS = INTERNAL_LINK_GROUPS.flatMap((g) => g.items);

const isVideoUrl = (u: string) => /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(u ?? "");

const empty: Partial<Slide> = {
  tag: "",
  title: "",
  subtitle: "",
  image_url: "",
  cta_label: "",
  cta_href: "",
  sort_order: 0,
  is_active: true,
  title_size: "lg",
  duration_ms: 9000,
};

function HeroAdmin() {
  const [items, setItems] = useState<Slide[]>([]);
  const [editing, setEditing] = useState<Partial<Slide> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    const { data } = await supabase.from("hero_slides").select("*").order("sort_order");
    setItems((data ?? []) as Slide[]);
  }
  useEffect(() => { load(); }, []);

  async function uploadImage(file: File): Promise<string> {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const path = `${userId}/hero/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    const { error } = await supabase.storage.from("media").upload(path, file, { contentType: file.type });
    if (error) throw error;
    const { data } = await supabase.storage.from("media").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
    return data?.signedUrl ?? "";
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.files?.[0];
    if (!raw || !editing) return;
    setBusy(true);
    setErr(null);
    try {
      const isVideo = raw.type.startsWith("video/");
      if (isVideo && raw.size > 200 * 1024 * 1024) throw new Error("El video supera los 200 MB.");
      const file = isVideo
        ? raw
        : await optimizeImage(raw, { maxDim: 1920, quality: 0.82, mimeType: "image/webp" });
      const url = await uploadImage(file);
      setEditing({ ...editing, image_url: url });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error subiendo imagen");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!editing) return;
    if (!editing.title || !editing.image_url) {
      setErr("Título e imagen son obligatorios.");
      return;
    }
    setErr(null);
    const payload = {
      tag: editing.tag || null,
      title: editing.title,
      subtitle: editing.subtitle || null,
      image_url: editing.image_url,
      cta_label: editing.cta_label || null,
      cta_href: editing.cta_href || null,
      sort_order: editing.sort_order ?? 0,
      is_active: !!editing.is_active,
      title_size: editing.title_size || "lg",
      duration_ms: Math.max(6000, Number(editing.duration_ms) || 9000),
    };
    const q = editing.id
      ? supabase.from("hero_slides").update(payload).eq("id", editing.id)
      : supabase.from("hero_slides").insert(payload);
    const { error } = await q;
    if (error) { setErr(error.message); return; }
    setEditing(null);
    load();
  }

  async function remove(s: Slide) {
    if (!confirm(`Eliminar slide "${s.title}"?`)) return;
    await supabase.from("hero_slides").delete().eq("id", s.id);
    load();
  }

  const lookup = useActorEmails(items.flatMap((s) => [s.created_by, s.updated_by]));

  if (editing) {
    return (
      <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4 max-w-3xl">
        <h2 className="font-bold text-lg">{editing.id ? "Editar" : "Nuevo"} slide del hero</h2>
        <p className="text-xs text-[var(--color-on-surface-variant)]">
          Imagen recomendada: <strong>1200 × 675 px</strong> (16:9). Hasta 1920 × 1080 si ocupa pantalla completa.
        </p>

        <Field label="Imagen o video (16:9)">
          <div className="space-y-2">
            {editing.image_url && (
              <div className="aspect-video bg-[var(--color-surface-container-low)] overflow-hidden">
                {isVideoUrl(editing.image_url) ? (
                  <video src={editing.image_url} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                ) : (
                  <img src={editing.image_url} alt="preview" className="w-full h-full object-cover" />
                )}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onFile} disabled={busy} className="text-sm" />
            <p className="text-[11px] text-[var(--color-on-surface-variant)]">Videos MP4/WebM hasta 200 MB. Se reproducen en bucle y sin sonido.</p>
            {busy && <p className="text-xs">Subiendo...</p>}
            <input
              value={editing.image_url ?? ""}
              onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
              placeholder="…o pega una URL"
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-xs font-mono"
            />
          </div>
        </Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Tag (etiqueta pequeña)">
            <input value={editing.tag ?? ""} onChange={(e) => setEditing({ ...editing, tag: e.target.value })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
          </Field>
          <Field label="Orden">
            <input type="number" value={editing.sort_order ?? 0}
              onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
          </Field>
        </div>

        <Field label="Título">
          <input value={editing.title ?? ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
        </Field>
        <Field label="Subtítulo / descripción">
          <textarea value={editing.subtitle ?? ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
            rows={3} className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
        </Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Tamaño del título">
            <select
              value={editing.title_size ?? "lg"}
              onChange={(e) => setEditing({ ...editing, title_size: e.target.value })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2 bg-white"
            >
              <option value="lg">Grande (por defecto)</option>
              <option value="md">Mediano</option>
              <option value="sm">Pequeño (títulos largos)</option>
            </select>
            <p className="text-[11px] text-[var(--color-on-surface-variant)] mt-1">
              Usa mediano o pequeño si el título es largo y se ve cortado.
            </p>
          </Field>
          <Field label="Segundos en pantalla">
            <input
              type="number"
              min={6}
              max={60}
              value={Math.round((editing.duration_ms ?? 9000) / 1000)}
              onChange={(e) => setEditing({ ...editing, duration_ms: Math.max(6, Number(e.target.value) || 6) * 1000 })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
            />
            <p className="text-[11px] text-[var(--color-on-surface-variant)] mt-1">Mínimo 6 segundos.</p>
          </Field>
        </div>



        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Texto del botón (CTA)">
            <input value={editing.cta_label ?? ""} onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
          </Field>
          <Field label="Link del botón">
            <div className="space-y-2">
              <select
                value={INTERNAL_LINKS.some((l) => l.href === editing.cta_href) ? editing.cta_href ?? "" : "__external__"}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__external__") setEditing({ ...editing, cta_href: "" });
                  else setEditing({ ...editing, cta_href: v });
                }}
                className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-sm bg-white"
              >
                <option value="">— Selecciona una página interna —</option>
                {INTERNAL_LINK_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.items.map((l) => (
                      <option key={l.href} value={l.href}>{l.label}</option>
                    ))}
                  </optgroup>
                ))}
                <option value="__external__">🔗 Enlace externo (https://…)</option>
              </select>
              <input
                value={editing.cta_href ?? ""}
                onChange={(e) => setEditing({ ...editing, cta_href: e.target.value })}
                placeholder="https://ejemplo.com o /ruta-interna"
                className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-xs"
              />
              <p className="text-[11px] text-[var(--color-on-surface-variant)]">
                Elige una página del sitio o escribe una URL externa completa.
              </p>
            </div>
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!editing.is_active}
            onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
          Activo (visible en la home)
        </label>

        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">Guardar</button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[var(--color-outline-variant)]">Cancelar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-bold text-lg">Carrusel Hero ({items.length})</h2>
          <p className="text-xs text-[var(--color-on-surface-variant)]">Imágenes 16:9 · 1200×675 recomendado.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">
          + Nuevo slide
        </button>
      </div>
      <div className="grid gap-4">
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-[var(--color-outline-variant)] p-4 flex gap-4">
            <div className="w-48 aspect-video bg-[var(--color-surface-container-low)] flex-shrink-0 overflow-hidden">
              {s.image_url && (isVideoUrl(s.image_url)
                ? <video src={s.image_url} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                : <img src={s.image_url} alt="" className="w-full h-full object-cover" />)}
            </div>
            <div className="flex-1">
              {s.tag && <span className="text-xs uppercase tracking-widest text-[var(--color-brand-coral)] font-bold">{s.tag}</span>}
              <h3 className="font-bold text-lg">{s.title}</h3>
              {s.subtitle && <p className="text-sm text-[var(--color-on-surface-variant)] line-clamp-2">{s.subtitle}</p>}
              <div className="text-xs mt-2">
                Orden: {s.sort_order} · {s.is_active ? <span className="text-green-700 font-bold">Activo</span> : <span className="text-[var(--color-on-surface-variant)]">Oculto</span>}
              </div>
              <div className="text-[11px] text-[var(--color-on-surface-variant)] mt-1">
                {fmtAuthor(lookup, s.created_by, s.updated_by)}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => setEditing(s)} className="text-xs underline">Editar</button>
              <button onClick={() => remove(s)} className="text-xs text-red-600">Eliminar</button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-12 text-[var(--color-on-surface-variant)] bg-white border border-[var(--color-outline-variant)]">
            Aún no hay slides. La home usará los valores por defecto.
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-1">{label}</div>
      {children}
    </label>
  );
}
